import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Facebook, Instagram, Youtube, Send, Paperclip, X } from "lucide-react";
import { CONTACT_INFO, SERVICES, SOCIAL_LINKS } from "../data/siteData";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
    const [form, setForm] = useState(initialForm);
    const [file, setFile] = useState(null);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            let attachment = {};
            if (file) {
                const data = new FormData();
                data.append("file", file);
                const res = await axios.post(`${API}/upload`, data);
                attachment = { attachment_name: res.data.filename, attachment_path: res.data.path };
            }
            await axios.post(`${API}/contact`, { ...form, phone: form.phone || null, service: form.service || null, ...attachment });
            toast.success("Messaggio inviato! Ti ricontatteremo al più presto.");
            setForm(initialForm);
            setFile(null);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Invio non riuscito. Riprova o scrivici via email.");
        } finally {
            setSending(false);
        }
    };

    const inputCls = "w-full bg-white border border-zinc-300 px-4 py-3.5 text-sm text-[#0E0E10] placeholder:text-zinc-400 focus:outline-none focus:border-[#FF0066] transition-colors duration-200";

    return (
        <section id="contatti" data-testid="contact-section" className="py-16 sm:py-24 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="max-w-2xl">
                    <p className="font-mono-label text-xs text-[#FF0066] mb-4">Contatti</p>
                    <h2 data-testid="contact-title" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-[#0E0E10]">
                        Raccontaci il tuo progetto
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-zinc-600 leading-relaxed">
                        Compila il modulo o vieni a trovarci in laboratorio: ti risponderemo al più presto.
                    </p>
                </Reveal>

                <div className="mt-12 grid lg:grid-cols-12 gap-10">
                    <Reveal delay={0.1} className="lg:col-span-7">
                        <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white border border-zinc-200 p-6 sm:p-8">
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="contact-name" className="block text-xs font-mono-label text-zinc-500 mb-2">Nome e Cognome *</label>
                                    <input id="contact-name" data-testid="contact-input-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Mario Rossi" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="block text-xs font-mono-label text-zinc-500 mb-2">Email *</label>
                                    <input id="contact-email" type="email" data-testid="contact-input-email" required value={form.email} onChange={set("email")} placeholder="mario@esempio.it" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="contact-phone" className="block text-xs font-mono-label text-zinc-500 mb-2">Telefono</label>
                                    <input id="contact-phone" type="tel" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} placeholder="+39 ..." className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="contact-service" className="block text-xs font-mono-label text-zinc-500 mb-2">Servizio di interesse</label>
                                    <select id="contact-service" data-testid="contact-select-service" value={form.service} onChange={set("service")} className={inputCls}>
                                        <option value="">Seleziona un servizio</option>
                                        {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                                        <option value="Altro">Altro</option>
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="contact-message" className="block text-xs font-mono-label text-zinc-500 mb-2">Messaggio *</label>
                                    <textarea id="contact-message" data-testid="contact-textarea-message" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder="Descrivici la tua idea: cosa vorresti realizzare, tempistiche, quantità..." className={`${inputCls} resize-y`} />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-xs font-mono-label text-zinc-500 mb-2">Allega un file (logo, grafica, foto — max 15 MB)</label>
                                    {file ? (
                                        <div data-testid="contact-file-selected" className="flex items-center justify-between bg-[#FAF8F5] border border-zinc-300 px-4 py-3.5">
                                            <span className="flex items-center gap-2 text-sm text-[#0E0E10] truncate">
                                                <Paperclip size={15} className="text-[#FF0066] shrink-0" />
                                                <span className="truncate">{file.name}</span>
                                                <span className="text-xs text-zinc-400 shrink-0">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                                            </span>
                                            <button type="button" data-testid="contact-file-remove" onClick={() => setFile(null)} className="p-1 text-zinc-400 hover:text-[#FF0066] transition-colors" aria-label="Rimuovi file">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label data-testid="contact-file-dropzone" className="flex items-center justify-center gap-2 border border-dashed border-zinc-300 px-4 py-4 text-sm text-zinc-500 cursor-pointer hover:border-[#FF0066] hover:text-[#FF0066] transition-colors duration-200">
                                            <Paperclip size={15} />
                                            Scegli un file da allegare
                                            <input
                                                type="file"
                                                data-testid="contact-input-file"
                                                className="hidden"
                                                accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.zip,.ai,.psd,.eps,.tif,.tiff,.doc,.docx,.txt"
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    if (!f) return;
                                                    if (f.size > 15 * 1024 * 1024) {
                                                        toast.error("File troppo grande: massimo 15 MB.");
                                                        e.target.value = "";
                                                        return;
                                                    }
                                                    setFile(f);
                                                }}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-submit-button"
                                disabled={sending}
                                className="mt-6 inline-flex items-center gap-2 bg-[#0E0E10] text-white font-semibold text-sm px-8 py-4 hover:bg-[#FF0066] disabled:opacity-60 transition-colors duration-300"
                            >
                                {sending ? "Invio in corso..." : "Invia richiesta"} <Send size={15} />
                            </button>
                        </form>
                    </Reveal>

                    <div className="lg:col-span-5 flex flex-col gap-6">
                        <Reveal delay={0.2}>
                            <div className="bg-[#0E0E10] text-white p-6 sm:p-8" data-testid="contact-info-card">
                                <h3 className="font-heading text-lg font-bold">Studio Arti Grafiche</h3>
                                <ul className="mt-5 space-y-4 text-sm">
                                    <li className="flex items-start gap-3">
                                        <MapPin size={17} className="text-[#FFCC00] mt-0.5 shrink-0" />
                                        <span className="text-zinc-300">{CONTACT_INFO.address}</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Phone size={17} className="text-[#FFCC00] mt-0.5 shrink-0" />
                                        <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="text-zinc-300 hover:text-white transition-colors">{CONTACT_INFO.phone}</a>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Mail size={17} className="text-[#FFCC00] mt-0.5 shrink-0" />
                                        <a href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email-link" className="text-zinc-300 hover:text-white transition-colors">{CONTACT_INFO.email}</a>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Clock size={17} className="text-[#FFCC00] mt-0.5 shrink-0" />
                                        <span className="text-zinc-300">{CONTACT_INFO.hours}</span>
                                    </li>
                                </ul>
                                <div className="mt-6 pt-6 border-t border-zinc-800 flex items-center gap-3">
                                    <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-link-facebook" aria-label="Facebook" className="p-2.5 border border-zinc-700 text-zinc-300 hover:border-[#FF0066] hover:text-[#FF0066] transition-colors duration-200">
                                        <Facebook size={17} />
                                    </a>
                                    <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-link-instagram" aria-label="Instagram" className="p-2.5 border border-zinc-700 text-zinc-300 hover:border-[#FF0066] hover:text-[#FF0066] transition-colors duration-200">
                                        <Instagram size={17} />
                                    </a>
                                    <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-link-youtube" aria-label="YouTube" className="p-2.5 border border-zinc-700 text-zinc-300 hover:border-[#FF0066] hover:text-[#FF0066] transition-colors duration-200">
                                        <Youtube size={17} />
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.28}>
                            <div className="border border-zinc-200 overflow-hidden" data-testid="contact-map">
                                <iframe
                                    title="Mappa Studio Arti Grafiche — Via Euclide 5, Gela"
                                    src="https://www.google.com/maps?q=Via+Euclide+5,+93012+Gela+CL,+Italia&output=embed"
                                    className="w-full h-64 border-0"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
